import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdOutlineDirectionsBike, MdAdd, MdEdit, MdDelete, MdCheck, MdClose } from "react-icons/md";

const Motorcycles = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBike, setEditingBike] = useState(null);
  
  // Form fields
  const [form, setForm] = useState({
    name: "",
    tag_line: "",
    rpm: "",
    power: "",
    torque: "",
    price: "",
    badge: "",
    description: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchBikes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/motorcycles");
      setBikes(response.data || []);
    } catch (err) {
      console.error("Error fetching motorcycles:", err);
      setError("Failed to fetch motorcycles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBikes();
  }, []);

  const handleOpenAdd = () => {
    setEditingBike(null);
    setForm({
      name: "",
      tag_line: "",
      rpm: "",
      power: "",
      torque: "",
      price: "",
      badge: "",
      description: "",
    });
    setImageFile(null);
    setImagePreview("");
    setError("");
    setModalOpen(true);
  };

  const handleOpenEdit = (bike) => {
    setEditingBike(bike);
    setForm({
      name: bike.name || "",
      tag_line: bike.tag_line || "",
      rpm: bike.rpm || "",
      power: bike.power || "",
      torque: bike.torque || "",
      price: bike.price || "",
      badge: bike.badge || "",
      description: bike.description || "",
    });
    setImageFile(null);
    setImagePreview(bike.image ? `${axiosInstance.defaults.baseURL}/${bike.image}` : "");
    setError("");
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name) {
      setError("Name is required.");
      return;
    }

    try {
      const data = new FormData();
      Object.keys(form).forEach((key) => {
        data.append(key, form[key]);
      });
      if (imageFile) {
        data.append("image", imageFile);
      }

      let res;
      if (editingBike) {
        res = await axiosInstance.post(`/motorcycles/${editingBike.id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axiosInstance.post("/motorcycles", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data.success) {
        setSuccessMsg(editingBike ? "Motorcycle updated!" : "Motorcycle added!");
        setModalOpen(false);
        fetchBikes();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(res.data.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save motorcycle details.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this motorcycle?")) return;
    try {
      const res = await axiosInstance.delete(`/motorcycles/${id}`);
      if (res.data.success) {
        setSuccessMsg("Motorcycle deleted!");
        fetchBikes();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete motorcycle.");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const res = await axiosInstance.post(`/motorcycles/toggle/${id}`);
      if (res.data.success) {
        fetchBikes();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Admin</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">Motorcycles</h1>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2.5 rounded-lg text-sm font-bold shadow-md transition-all"
        >
          <MdAdd className="text-lg" />
          Add Motorcycle
        </button>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : bikes.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdOutlineDirectionsBike className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No motorcycles found. Click "Add Motorcycle" to create one.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="p-4">Image</th>
                  <th className="p-4">Model Name</th>
                  <th className="p-4">Badge</th>
                  <th className="p-4">Specs (RPM / HP / Torque)</th>
                  <th className="p-4">Price</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {bikes.map((bike) => {
                  const imgUrl = bike.image 
                    ? `${axiosInstance.defaults.baseURL}/${bike.image}`
                    : null;
                  
                  return (
                    <tr key={bike.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-4">
                        {imgUrl ? (
                          <img src={imgUrl} alt={bike.name} className="h-12 w-20 object-contain bg-slate-100 rounded" />
                        ) : (
                          <div className="h-12 w-20 bg-slate-100 flex items-center justify-center text-slate-400 text-xs rounded">No Image</div>
                        )}
                      </td>
                      <td className="p-4 font-semibold text-slate-900">
                        <div>{bike.name}</div>
                        <div className="text-xs text-slate-400 font-normal mt-0.5">{bike.tag_line}</div>
                      </td>
                      <td className="p-4">
                        {bike.badge ? (
                          <span className="inline-block bg-amber-100 text-amber-800 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded">
                            {bike.badge}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-4 text-slate-600 font-medium">
                        {bike.rpm || "-"} / {bike.power || "-"} / {bike.torque || "-"}
                      </td>
                      <td className="p-4 font-bold text-slate-900">{bike.price || "Contact Us"}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleActive(bike.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm transition-all ${
                            parseInt(bike.is_active) === 1
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                              : "bg-rose-50 text-rose-700 border border-rose-100"
                          }`}
                        >
                          {parseInt(bike.is_active) === 1 ? (
                            <>
                              <MdCheck className="text-sm" /> Active
                            </>
                          ) : (
                            <>
                              <MdClose className="text-sm" /> Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="p-4 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => handleOpenEdit(bike)}
                          className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition"
                          title="Edit"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => handleDelete(bike.id)}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-slate-100 rounded-lg transition"
                          title="Delete"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-200 my-8">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-950">
                {editingBike ? "Edit Motorcycle Details" : "Add New Motorcycle"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model Name</span>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Bullet 350"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tag Line</span>
                  <input
                    type="text"
                    placeholder="e.g. Classic Legend"
                    value={form.tag_line}
                    onChange={(e) => setForm({ ...form, tag_line: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">RPM</span>
                  <input
                    type="text"
                    placeholder="e.g. 6,100 rpm"
                    value={form.rpm}
                    onChange={(e) => setForm({ ...form, rpm: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Power</span>
                  <input
                    type="text"
                    placeholder="e.g. 20.2 bhp"
                    value={form.power}
                    onChange={(e) => setForm({ ...form, power: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Torque</span>
                  <input
                    type="text"
                    placeholder="e.g. 27 Nm"
                    value={form.torque}
                    onChange={(e) => setForm({ ...form, torque: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Price (INR)</span>
                  <input
                    type="text"
                    placeholder="e.g. ₹1,74,875"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1 sm:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Badge/Offer Tag</span>
                  <input
                    type="text"
                    placeholder="e.g. Best Seller, Save 15%"
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>
              </div>

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</span>
                <textarea
                  rows="3"
                  placeholder="Enter detailed model specifications or features..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all resize-none"
                />
              </label>

              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">Motorcycle Photo</span>
                <div className="flex flex-col sm:flex-row items-center gap-4 border border-dashed border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="h-24 w-36 object-contain bg-white border border-slate-200 rounded p-1 shrink-0" />
                  ) : (
                    <div className="h-24 w-36 bg-slate-100 flex items-center justify-center text-slate-400 text-[11px] rounded border shrink-0">
                      No Photo Selected
                    </div>
                  )}
                  <div className="flex-1 w-full text-center sm:text-left">
                    <input
                      type="file"
                      accept="image/*"
                      id="bike-image-file"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="bike-image-file"
                      className="inline-block bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer transition shadow-sm"
                    >
                      {imagePreview ? "Change Photo" : "Upload Photo"}
                    </label>
                    <p className="text-[10px] text-slate-400 mt-2">Supports JPG, PNG or WEBP. Max file size 5MB.</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-sm font-bold shadow-md transition"
                >
                  {editingBike ? "Save Changes" : "Create Model"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Motorcycles;
